<template class="text_google">
  <v-app>
    <!-- Enhanced System Bar -->
    <v-system-bar
      class="noprint"
      color="primary"
      dark
      height="60"
      app
      lights-out
    >
      <v-container fluid class="pa-0">
        <v-row align="center" no-gutters>
          <v-col cols="auto">
            <v-toolbar-items class="ml-2">
              <v-btn
                @click="handlePrint"
                class="elevation-0 mr-2"
                color="white"
                outlined
                large
                :loading="printing"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-printer</v-icon>
                พิมพ์รายงาน
              </v-btn>
              
              <v-btn
                @click="exportToWord"
                class="elevation-0 mr-2"
                color="success"
                large
                :loading="exporting"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-word</v-icon>
                Export Word
              </v-btn>

              <v-btn
                @click="exportToPDF"
                class="elevation-0 mr-2"
                color="error"
                large
                :loading="exportingPDF"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>

              <v-btn
                @click="exportToExcel"
                class="elevation-0"
                color="orange"
                large
                :loading="exportingExcel"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Export Excel
              </v-btn>
            </v-toolbar-items>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="auto">
            <v-chip
              class="mr-2"
              :color="statusChipColor"
              small
              outlined
            >
              <v-icon left small>{{ statusIcon }}</v-icon>
              {{ statusText }}
            </v-chip>

            <v-btn
              @click="refreshData"
              icon
              small
              :loading="refreshing"
              color="white"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-system-bar>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" opacity="0.8" z-index="9999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 white--text">{{ loadingMessage }}</div>
      </div>
    </v-overlay>

    <!-- Error Alert -->
    <v-alert
      v-if="error"
      type="error"
      dismissible
      @input="error = null"
      class="ma-4 noprint"
    >
      {{ error }}
    </v-alert>

    <!-- Report Content -->
    <div id="exportContent" class="export-content" :style="pageOrientation">
      <div class="page">
        <!-- Report Header -->
        <div class="report-header text-center mb-6">
          <div class="logo-container mb-4">
            <v-img 
              :src="logoUrl" 
              max-height="80" 
              max-width="60" 
              contain
              class="mx-auto"
              @error="onLogoError"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </div>
          
          <h1 class="head mb-2">
            แบบรายงานข้อมูลอัตรากำลังสถานศึกษา ประจำปี {{ displayYear }}
          </h1>
          <h2 class="head mb-2">{{ manpowerData.college_name || 'กำลังโหลด...' }}</h2>
          <div class="divider-line">___________________________</div>
          
          <!-- Quick Stats (Print Hidden) -->
          <v-row class="mt-4 noprint" v-if="!loading && hasData">
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon color="primary" size="32" class="mb-2">mdi-account-tie</v-icon>
                <div class="text-h6">{{ formatNumber(totalManpower) }}</div>
                <div class="text-caption">อัตรากำลังรวม</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon color="success" size="32" class="mb-2">mdi-home-group</v-icon>
                <div class="text-h6">{{ formatNumber(totalClassrooms) }}</div>
                <div class="text-caption">จำนวนห้องเรียน</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon 
                  :color="getShortageStatusColor(totalShortage)" 
                  size="32" 
                  class="mb-2"
                >
                  {{ getShortageStatusIcon(totalShortage) }}
                </v-icon>
                <div 
                  class="text-h6" 
                  :class="`${getShortageStatusColor(totalShortage)}--text`"
                >
                  {{ formatShortage(totalShortage) }}
                </div>
                <div class="text-caption">สถานะอัตรากำลัง</div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Main Manpower Table -->
        <v-card outlined elevation="2" class="mb-6">
          <v-card-text class="pa-0">
            <div class="table-wrapper">
              <v-simple-table 
                class="manpower-table"
                :loading="loading"
                dense
              >
                <template v-slot:loading>
                  <div class="text-center pa-4">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <div class="mt-2">กำลังโหลดข้อมูล...</div>
                  </div>
                </template>

                <thead>
                  <tr class="table-header">
                    <th class="text-center header-cell">
                      <span class="bold16">ประเภทอัตรา</span>
                    </th>
                    <th class="text-center header-cell">
                      <span class="bold16">มีจริง</span>
                    </th>
                    <th class="text-center header-cell">
                      <span class="bold16">จำนวนห้องเรียน</span>
                    </th>
                    <th class="text-center header-cell">
                      <span class="bold16">เกณฑ์</span>
                    </th>
                    <th class="text-center header-cell">
                      <span class="bold16">อัตรากำลังรวม</span>
                    </th>
                    <th class="text-center header-cell">
                      <span class="bold16">(-ขาด/+เกิน)</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <!-- No Data Message -->
                  <tr v-if="!loading && !hasData">
                    <td colspan="6" class="text-center pa-4">
                      <v-icon size="48" color="grey lighten-1" class="mb-2">
                        mdi-database-off
                      </v-icon>
                      <div class="text-body-1 grey--text">
                        ไม่มีข้อมูลอัตรากำลัง
                      </div>
                    </td>
                  </tr>

                  <!-- Management Section -->
                  <template v-if="hasData">
                    <!-- Director Row -->
                    <tr class="management-row">
                      <td class="text-center font-weight-bold management-cell">
                        <v-icon left small color="purple">mdi-account-star</v-icon>
                        ผอ.สถานศึกษา
                      </td>
                      <td class="text-center data-cell">
                        <v-chip small color="primary" dark>
                          {{ formatNumber(manpowerData.rate_work_g_dr) }}
                        </v-chip>
                      </td>
                      <td class="text-center data-cell" rowspan="2">
                        <v-chip small color="info" dark>
                          {{ formatNumber(manpowerData.rate_room_cal_all) }}
                        </v-chip>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_criterion_dr) }}</span>
                      </td>
                      <td class="text-center data-cell" rowspan="2">
                        <v-chip small color="success" dark>
                          {{ formatNumber(manpowerData.rate_man_power_teach_all) }}
                        </v-chip>
                      </td>
                      <td class="text-center data-cell">
                        <v-chip 
                          small 
                          :color="getShortageColor(manpowerData.rate_criterion_incomplete_dr)"
                          dark
                        >
                          {{ formatShortage(manpowerData.rate_criterion_incomplete_dr) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Deputy Director Row -->
                    <tr class="management-row">
                      <td class="text-center font-weight-bold management-cell">
                        <v-icon left small color="indigo">mdi-account-supervisor</v-icon>
                        รอง ผอ.สถานศึกษา
                      </td>
                      <td class="text-center data-cell">
                        <v-chip small color="primary" dark>
                          {{ formatNumber(manpowerData.rate_work_g_sedr) }}
                        </v-chip>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_criterion_sedr) }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <v-chip 
                          small 
                          :color="getShortageColor(manpowerData.rate_criterion_incomplete_sedr)"
                          dark
                        >
                          {{ formatShortage(manpowerData.rate_criterion_incomplete_sedr) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Teachers Section Header -->
                    <tr class="teachers-header">
                      <td class="text-center font-weight-bold teachers-header-cell" rowspan="2">
                        <v-icon left small color="white">mdi-school</v-icon>
                        ครูผู้สอน (รวม)
                      </td>
                      <td class="text-center font-weight-bold teachers-header-cell">
                        {{ formatNumber(manpowerData.rate_teach_official_all) }}
                      </td>
                      <td class="text-center font-weight-bold teachers-header-cell">
                        {{ formatNumber(manpowerData.rate_teach_civil_all) }}
                      </td>
                      <td class="text-center font-weight-bold teachers-header-cell">
                        {{ formatNumber(manpowerData.rate_criterion_teach_all) }}
                      </td>
                      <td class="text-center font-weight-bold teachers-header-cell">
                        {{ formatNumber(manpowerData.rate_criterion_teach_alls) }}
                      </td>
                      <td class="text-center font-weight-bold teachers-header-cell" rowspan="2">
                        <v-chip 
                          small 
                          :color="getShortageColor(manpowerData.rate_criterion_incomplete_teach_all)"
                          dark
                        >
                          {{ formatShortage(manpowerData.rate_criterion_incomplete_teach_all) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Teachers Sub-header -->
                    <tr class="teachers-subheader">
                      <td class="text-center sub-header-cell">ข้าราชการ</td>
                      <td class="text-center sub-header-cell">พนักงานราชการ</td>
                      <td class="text-center sub-header-cell">ข้าราชการ</td>
                      <td class="text-center sub-header-cell">พนักงานราชการ</td>
                    </tr>

                    <!-- Subject Categories -->
                    <tr
                      v-for="(category, index) in subjectCategories"
                      :key="category.key"
                      class="subject-row"
                      :class="{ 'row-highlight': index % 2 === 0 }"
                    >
                      <td class="subject-name">
                        <div class="d-flex align-center">
                          <v-icon 
                            left 
                            small 
                            :color="category.color"
                          >
                            {{ category.icon }}
                          </v-icon>
                          <span class="regular16">{{ category.name }}</span>
                        </div>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ getSubjectData(category.key, 'ta') }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ getSubjectData(category.key, 'tb') }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ getSubjectData(category.key, 'ta_official') }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ getSubjectData(category.key, 'tb_civil') }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <v-chip 
                          small 
                          :color="getShortageColor(getSubjectData(category.key, 'incomplete'))"
                          dark
                        >
                          {{ formatShortage(getSubjectData(category.key, 'incomplete')) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Support Staff Row -->
                    <tr class="support-staff-row">
                      <td class="font-weight-bold support-staff-cell">
                        <v-icon left small color="orange">mdi-account-group</v-icon>
                        <strong>บุคลากรสนับสนุนการสอน</strong>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_work_g_sup_ta) }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_work_g_sup_tb) }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_work_g_sup_ta_official) }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <span class="regular16">{{ formatNumber(manpowerData.rate_work_g_sup_tb_civil) }}</span>
                      </td>
                      <td class="text-center data-cell">
                        <v-chip 
                          small 
                          :color="getShortageColor(manpowerData.rate_work_g_sup_incomplete)"
                          dark
                        >
                          {{ formatShortage(manpowerData.rate_work_g_sup_incomplete) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Total Summary Row -->
                    <tr class="total-summary-row">
                      <td class="text-center font-weight-bold total-summary-cell">
                        <v-icon left small color="white">mdi-calculator</v-icon>
                        <h3>รวมอัตรา</h3>
                      </td>
                      <td class="text-center font-weight-bold total-summary-cell" colspan="2">
                        <h3>{{ formatNumber(manpowerData.rate_man_power_all) }}</h3>
                      </td>
                      <td class="text-center font-weight-bold total-summary-cell">
                        {{ formatNumber(manpowerData.rate_criterion_all) }}
                      </td>
                      <td class="text-center font-weight-bold total-summary-cell"></td>
                      <td class="text-center font-weight-bold total-summary-cell">
                        <v-chip 
                          small 
                          :color="getShortageColor(manpowerData.rate_criterion_incomplete_all)"
                          dark
                        >
                          {{ formatShortage(manpowerData.rate_criterion_incomplete_all) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Employee Categories -->
                    <tr class="employee-header-row">
                      <td rowspan="2" class="text-center employee-header-cell">
                        <v-icon left small color="white">mdi-briefcase</v-icon>
                        ลูกจ้างประจำ/ชั่วคราว
                      </td>
                      <td colspan="2" class="text-center employee-header-cell">
                        ลูกจ้างประจำ
                      </td>
                      <td colspan="2" class="text-center employee-header-cell">
                        ครูจ้างสอน
                      </td>
                      <td class="text-center employee-header-cell">
                        เจ้าหน้าที่
                      </td>
                    </tr>

                    <!-- Employee Data Row -->
                    <tr class="employee-data-row">
                      <td colspan="2" class="text-center employee-data-cell">
                        <v-chip small color="teal" dark>
                          {{ formatNumber(manpowerData.rate_work_g_pte_ta) }}
                        </v-chip>
                      </td>
                      <td colspan="2" class="text-center employee-data-cell">
                        <v-chip small color="deep-purple" dark>
                          {{ formatNumber(manpowerData.rate_work_g_pte_tc) }}
                        </v-chip>
                      </td>
                      <td class="text-center employee-data-cell">
                        <v-chip small color="brown" dark>
                          {{ formatNumber(manpowerData.rate_work_g_pte_tb) }}
                        </v-chip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>

        <!-- Analysis Summary (Print Hidden) -->
        <v-row class="mb-6 noprint" v-if="!loading && hasData">
          <v-col cols="12" md="4" v-for="analysis in analysisData" :key="analysis.title">
            <v-card outlined class="text-center pa-3">
              <v-icon :color="analysis.color" size="28" class="mb-2">
                {{ analysis.icon }}
              </v-icon>
              <div class="text-h6 font-weight-bold" :class="`${analysis.color}--text`">
                {{ analysis.value }}
              </div>
              <div class="text-caption">{{ analysis.title }}</div>
              <div class="text-caption grey--text mt-1">{{ analysis.description }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Signature Section -->
        <div class="signature-section mt-8">
          <v-row>
            <v-col cols="12" md="8"></v-col>
            <v-col cols="12" md="4">
              <div class="signature-box text-center">
                <div class="signature-line mb-3">
                  <span class="regular16">
                    ลงชื่อ.................................................................
                  </span>
                </div>
                <div class="signature-name mb-3">
                  <span class="regular16">
                    (.................................................)
                  </span>
                </div>
                <div class="signature-title">
                  <span class="regular16 font-weight-medium">
                    ผู้อำนวยการ{{ manpowerData.college_name || '' }}
                  </span>
                </div>
                
                <!-- Date and Print Info -->
                <div class="mt-4 text-caption grey--text noprint">
                  <div>วันที่พิมพ์: {{ currentDate }}</div>
                  <div>พิมพ์โดย: {{ currentUser }}</div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Report Footer -->
        <div class="report-footer mt-6">
          <v-divider class="mb-3"></v-divider>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-caption">
                <strong>หมายเหตุ:</strong>
                <ul class="mt-1">
                  <li>(-) หมายถึง ขาดอัตรากำลัง, (+) หมายถึง เกินอัตรากำลัง</li>
                  <li>ข้อมูลอัตรากำลังครูผู้สอนแยกตามประเภทวิชา</li>
                  <li>รวมบุคลากรสนับสนุนการสอนและลูกจ้าง</li>
                  <li>ข้อมูล ณ วันที่ {{ currentDate }}</li>
                </ul>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="text-caption">
                <div>รหัสเอกสาร: {{ documentId }}</div>
                <div>ปีการศึกษา: {{ displayYear }}</div>
                <div>หน้า: <span id="pageNumber">1</span></div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
      
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>
<script>
export default {
  name: "ImprovedManpowerReport",
  
  data() {
    return {
      // API Configuration
      ApiKey: "HRvec2021",
      
      // Loading States
      loading: false,
      refreshing: false,
      printing: false,
      exporting: false,
      exportingPDF: false,
      exportingExcel: false,
      loadingMessage: 'กำลังโหลดข้อมูล...',
      
      // Data
      manpowerData: {},
      
      // Error Handling
      error: null,
      logoError: false,
      
      // Notifications
      snackbar: {
        show: false,
        color: '',
        timeout: 5000,
        icon: '',
        text: ''
      },
      
      // Page Settings
      pageOrientation: 'mso-page-orientation: portrait;',
      
      // Subject Categories Configuration
      subjectCategories: [
        { key: 'gs', name: '-ประเภทวิชาสามัญ', icon: 'mdi-book-open-page-variant', color: 'blue' },
        { key: 'is', name: '-ประเภทวิชาอุตสาหกรรม', icon: 'mdi-factory', color: 'orange' },
        { key: 'cm', name: '-ประเภทวิชาพาณิชยกรรม', icon: 'mdi-briefcase', color: 'green' },
        { key: 'art', name: '-ประเภทวิชาศิลปกรรม', icon: 'mdi-palette', color: 'purple' },
        { key: 'em', name: '-ประเภทวิชาคหกรรม', icon: 'mdi-home-heart', color: 'pink' },
        { key: 'ag', name: '-ประเภทวิชาเกษตรกรรม', icon: 'mdi-sprout', color: 'green' },
        { key: 'fish', name: '-ประเภทวิชาประมง', icon: 'mdi-fish', color: 'blue' },
        { key: 'tour', name: '-ประเภทวิชาอุตสาหกรรมการท่องเที่ยว', icon: 'mdi-airplane', color: 'teal' },
        { key: 'textile', name: '-ประเภทวิชาอุตสาหกรรมสิ่งทอ', icon: 'mdi-tshirt-crew', color: 'indigo' },
        { key: 'insy', name: '-ประเภทวิชาเทคโนโลยีสารสนเทศ', icon: 'mdi-laptop', color: 'cyan' },
        { key: 'entertain', name: '-ประเภทวิชาอุตสาหกรรมบันเทิงและดนตรี', icon: 'mdi-music', color: 'deep-purple' },
        { key: 'navy', name: '-ประเภทวิชาพาณิชยนาวี', icon: 'mdi-ship-wheel', color: 'blue-grey' },
        { key: 'agriInno', name: '-ประเภทวิชาเทคโนโลยีและนวัตกรรมเกษตรอุตสาหกรรม', icon: 'mdi-leaf', color: 'light-green' },
        { key: 'sc', name: '-หลักสูตรระยะสั้น*', icon: 'mdi-clock-fast', color: 'amber' }
      ]
    };
  },

  computed: {
    // URL Parameters
    displayYear() {
      return this.$route.params.rate_work_g_year || new Date().getFullYear() + 543;
    },

    collegeCode() {
      return this.$route.params.college_code || '';
    },

    // Data Status
    hasData() {
      return Object.keys(this.manpowerData).length > 0;
    },

    statusChipColor() {
      if (this.loading) return 'warning';
      return this.hasData ? 'success' : 'error';
    },

    statusIcon() {
      if (this.loading) return 'mdi-loading';
      return this.hasData ? 'mdi-check' : 'mdi-alert';
    },

    statusText() {
      if (this.loading) return 'กำลังโหลด...';
      if (this.hasData) return 'ข้อมูลพร้อม';
      return 'ไม่มีข้อมูล';
    },

    // Logo URL
    logoUrl() {
      return this.logoError ? '/placeholder-logo.png' : '/ovec.png';
    },

    // Date and User Info
    currentDate() {
      return new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    currentUser() {
      const user = JSON.parse(localStorage.getItem("user") || '{}');
      return user.user_firstname || 'ระบบ';
    },

    documentId() {
      return `MAN-${this.collegeCode}-${this.displayYear}-${Date.now().toString().slice(-6)}`;
    },

    // Calculations
    totalManpower() {
      return parseInt(this.manpowerData.rate_man_power_all) || 0;
    },

    totalClassrooms() {
      return parseInt(this.manpowerData.rate_room_cal_all) || 0;
    },
totalShortage() {
      return parseInt(this.manpowerData.rate_criterion_incomplete_all) || 0;
    },

    analysisData() {
      const teacherRatio = this.totalClassrooms > 0 
        ? (this.totalManpower / this.totalClassrooms).toFixed(1)
        : '0';
      
      const managementCount = (parseInt(this.manpowerData.rate_work_g_dr) || 0) + 
                             (parseInt(this.manpowerData.rate_work_g_sedr) || 0);
      
      const teacherCount = parseInt(this.manpowerData.rate_teach_official_all) || 0;

      return [
        {
          title: 'อัตราครู:ห้องเรียน',
          value: `${teacherRatio}:1`,
          icon: 'mdi-calculator',
          color: 'info',
          description: 'ครูต่อห้องเรียน'
        },
        {
          title: 'ผู้บริหาร',
          value: managementCount.toString(),
          icon: 'mdi-account-star',
          color: 'purple',
          description: 'ผอ. + รองผอ.'
        },
        {
          title: 'ครูผู้สอนรวม',
          value: teacherCount.toLocaleString(),
          icon: 'mdi-school',
          color: 'success',
          description: 'ข้าราชการ + พนักงาน'
        }
      ];
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    // Initialization
    async initializeComponent() {
      this.loading = true;
      this.loadingMessage = 'กำลังโหลดข้อมูลรายงาน...';

      try {
        await this.loadManpowerData();
        
        if (this.hasData) {
          this.showSuccessMessage('โหลดข้อมูลรายงานเรียบร้อยแล้ว');
        } else {
          this.showWarningMessage('ไม่พบข้อมูลสำหรับปีการศึกษาที่เลือก');
        }
      } catch (error) {
        this.handleError('ไม่สามารถโหลดข้อมูลรายงานได้', error);
      } finally {
        this.loading = false;
      }
    },

    // Data Loading
    async loadManpowerData() {
      try {
        const result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_college_code: this.collegeCode,
          rate_work_g_year: this.displayYear
        });
        
        this.manpowerData = result.data || {};
      } catch (error) {
        console.error('Error loading manpower data:', error);
        throw error;
      }
    },

    async refreshData() {
      this.refreshing = true;
      try {
        await this.initializeComponent();
        this.showSuccessMessage('รีเฟรชข้อมูลเรียบร้อยแล้ว');
      } catch (error) {
        this.handleError('ไม่สามารถรีเฟรชข้อมูลได้', error);
      } finally {
        this.refreshing = false;
      }
    },

    getSubjectData(categoryKey, field) {
      const key = `rate_work_g_${categoryKey}_${field}`;
      const value = this.manpowerData[key];
      return this.formatNumber(value);
    },

    // Formatting Functions
    formatNumber(value) {
      const num = parseInt(value) || 0;
      return num === 0 ? '-' : num.toLocaleString();
    },

    formatShortage(value) {
      if (value === null || value === undefined || value === '-') return '-';
      const num = parseInt(value) || 0;
      if (num > 0) return `+${num}`;
      if (num < 0) return `${num}`;
      return '0';
    },

    // Styling Functions
    getShortageColor(value) {
      if (value === null || value === undefined || value === '-') return 'grey';
      const num = parseInt(value) || 0;
      if (num > 0) return 'success';
      if (num < 0) return 'error';
      return 'warning';
    },

    getShortageStatusColor(value) {
      if (value === null || value === undefined) return 'grey';
      const num = parseInt(value) || 0;
      if (num > 0) return 'success';
      if (num < 0) return 'error';
      return 'info';
    },

    getShortageStatusIcon(value) {
      if (value === null || value === undefined) return 'mdi-help';
      const num = parseInt(value) || 0;
      if (num > 0) return 'mdi-trending-up';
      if (num < 0) return 'mdi-trending-down';
      return 'mdi-equal';
    },

    // Export Functions
    async handlePrint() {
      this.printing = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        window.print();
        this.showSuccessMessage('เริ่มการพิมพ์แล้ว');
      } catch (error) {
        this.handleError('เกิดข้อผิดพลาดในการพิมพ์', error);
      } finally {
        this.printing = false;
      }
    },

    async exportToWord() {
      this.exporting = true;
      try {
        const element = document.getElementById('exportContent');
        const preHtml = `
          <html xmlns:o='urn:schemas-microsoft-com:office:office' 
                xmlns:w='urn:schemas-microsoft-com:office:word' 
                xmlns='http://www.w3.org/TR/REC-html40'>
          <head>
            <meta charset='utf-8'>
            <title>รายงานข้อมูลอัตรากำลังสถานศึกษา - ${this.manpowerData.college_name}</title>
            <style>
              body { font-family: 'TH SarabunIT๙', 'TH SarabunPSK'; font-size: 16pt; }
              table { border-collapse: collapse; width: 100%; }
              td, th { border: 1px solid black; padding: 8px; text-align: center; }
              .header-cell { background-color: #e8f4f8; font-weight: bold; }
              .management-cell { background-color: #f3e5f5; }
              .teachers-header-cell { background-color: #e8f5e8; }
              .bold16 { font-weight: bold; font-size: 16pt; }
              .regular16 { font-size: 16pt; }
            </style>
          </head>
          <body>
        `;
        const postHtml = '</body></html>';
        
        const cleanContent = element.innerHTML
          .replace(/class="[^"]*"/g, '')
          .replace(/<v-[^>]*>/g, '')
          .replace(/<\/v-[^>]*>/g, '')
          .replace(/noprint[^>]*>/g, 'style="display:none">');

        const html = preHtml + cleanContent + postHtml;
        const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `รายงานอัตรากำลัง_${this.collegeCode}_${this.displayYear}.doc`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        this.showSuccessMessage('ส่งออกไฟล์ Word เรียบร้อยแล้ว');
      } catch (error) {
        this.handleError('เกิดข้อผิดพลาดในการส่งออกไฟล์ Word', error);
      } finally {
        this.exporting = false;
      }
    },

    async exportToPDF() {
      this.exportingPDF = true;
      try {
        const originalTitle = document.title;
        document.title = `รายงานอัตรากำลัง_${this.collegeCode}_${this.displayYear}`;
        
        const printCSS = `
          <style media="print">
            @page { 
              size: A4 portrait; 
              margin: 1.5cm 1cm; 
            }
            .noprint { display: none !important; }
            body { font-size: 12pt; }
            table { font-size: 10pt; }
            .page { border: none; box-shadow: none; }
          </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', printCSS);
        window.print();
        
        setTimeout(() => {
          document.title = originalTitle;
        }, 1000);
        
        this.showSuccessMessage('เปิดหน้าต่างการพิมพ์เป็น PDF แล้ว');
      } catch (error) {
        this.handleError('เกิดข้อผิดพลาดในการส่งออกไฟล์ PDF', error);
      } finally {
        this.exportingPDF = false;
      }
    },

    async exportToExcel() {
      this.exportingExcel = true;
      try {
        let csvContent = 'ประเภทอัตรา,มีจริง,จำนวนห้องเรียน,เกณฑ์,อัตรากำลังรวม,ขาด/เกิน\n';
        
        // Management data
        csvContent += `"ผอ.สถานศึกษา","${this.manpowerData.rate_work_g_dr || 0}","${this.manpowerData.rate_room_cal_all || 0}","${this.manpowerData.rate_criterion_dr || 0}","${this.manpowerData.rate_man_power_teach_all || 0}","${this.formatShortage(this.manpowerData.rate_criterion_incomplete_dr)}"\n`;
        csvContent += `"รอง ผอ.สถานศึกษา","${this.manpowerData.rate_work_g_sedr || 0}","","${this.manpowerData.rate_criterion_sedr || 0}","","${this.formatShortage(this.manpowerData.rate_criterion_incomplete_sedr)}"\n`;
        
        // Teachers summary
        csvContent += `"ครูผู้สอน (รวม)","${this.manpowerData.rate_teach_official_all || 0}","${this.manpowerData.rate_teach_civil_all || 0}","${this.manpowerData.rate_criterion_teach_all || 0}","${this.manpowerData.rate_criterion_teach_alls || 0}","${this.formatShortage(this.manpowerData.rate_criterion_incomplete_teach_all)}"\n`;
        
        // Subject categories
        this.subjectCategories.forEach(category => {
          const ta = this.manpowerData[`rate_work_g_${category.key}_ta`] || 0;
          const tb = this.manpowerData[`rate_work_g_${category.key}_tb`] || 0;
          const ta_official = this.manpowerData[`rate_work_g_${category.key}_ta_official`] || 0;
          const tb_civil = this.manpowerData[`rate_work_g_${category.key}_tb_civil`] || 0;
          const incomplete = this.manpowerData[`rate_work_g_${category.key}_incomplete`] || 0;
          
          csvContent += `"${category.name}","${ta}","${tb}","${ta_official}","${tb_civil}","${this.formatShortage(incomplete)}"\n`;
        });

        // Support staff
        csvContent += `"บุคลากรสนับสนุนการสอน","${this.manpowerData.rate_work_g_sup_ta || 0}","${this.manpowerData.rate_work_g_sup_tb || 0}","${this.manpowerData.rate_work_g_sup_ta_official || 0}","${this.manpowerData.rate_work_g_sup_tb_civil || 0}","${this.formatShortage(this.manpowerData.rate_work_g_sup_incomplete)}"\n`;

        // Total
        csvContent += `"รวมอัตรา","${this.manpowerData.rate_man_power_all || 0}","","${this.manpowerData.rate_criterion_all || 0}","","${this.formatShortage(this.manpowerData.rate_criterion_incomplete_all)}"\n`;

        // Employee categories
        csvContent += `"ลูกจ้างประจำ","${this.manpowerData.rate_work_g_pte_ta || 0}","","","",""\n`;
        csvContent += `"ครูจ้างสอน","${this.manpowerData.rate_work_g_pte_tc || 0}","","","",""\n`;
        csvContent += `"เจ้าหน้าที่","${this.manpowerData.rate_work_g_pte_tb || 0}","","","",""\n`;

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `รายงานอัตรากำลัง_${this.collegeCode}_${this.displayYear}.csv`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        this.showSuccessMessage('ส่งออกไฟล์ Excel เรียบร้อยแล้ว');
      } catch (error) {
        this.handleError('เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel', error);
      } finally {
        this.exportingExcel = false;
      }
    },

    // Event Handlers
    onLogoError() {
      this.logoError = true;
    },

    // Notification Methods
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        color: 'success',
        text: message,
        icon: 'mdi-check-circle',
        timeout: 5000
      };
    },

    showWarningMessage(message) {
      this.snackbar = {
        show: true,
        color: 'warning',
        text: message,
        icon: 'mdi-alert',
        timeout: 6000
      };
    },

    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        color: 'error',
        text: message,
        icon: 'mdi-alert-circle',
        timeout: 8000
      };
    },

    // Error Handling
    handleError(message, error = null) {
      console.error('Report Error:', error);
      this.error = message;
      this.showErrorMessage(message);
    }
  },

  // Cleanup
  beforeDestroy() {
    // Clean up any resources if needed
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@100;300;400;500;600;700&display=swap");

/* Global Font Settings */
* {
  font-family: "TH SarabunIT๙", "TH SarabunPSK", "Sarabun", sans-serif;
  box-sizing: border-box;
}

body {
  font-family: "TH SarabunIT๙", "TH SarabunPSK", "Sarabun", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

/* Page Layout */
.export-content {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px 0;
}

.page {
  width: 100%;
  max-width: 800px;
  min-height: 29.7cm;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  margin: 0 auto;
  padding: 2cm 1.5cm;
  position: relative;
}

/* Typography */
.head {
  font-size: 18pt;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 12px;
  line-height: 1.3;
}

.regular12 {
  font-size: 14pt;
  font-weight: 400;
}

.regular16 {
  font-size: 16pt;
  font-weight: 400;
}

.bold16 {
  font-size: 16pt;
  font-weight: 600;
}

.blod12 {
  font-size: 14pt;
  font-weight: 600;
}

.blackRegula10 {
  font-size: 12pt;
  font-weight: 400;
}

.blackBold18 {
  font-size: 18pt;
  font-weight: 700;
}

/* Report Header */
.report-header {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.divider-line {
  font-size: 18pt;
  color: #1976d2;
  margin-top: 16px;
}

/* Table Styles */
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.manpower-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.manpower-table >>> table {
  border-radius: 12px;
}

/* Table Headers */
.table-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.header-cell {
  background: #1976d2 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 12px 8px !important;
  border: 1px solid #1565c0 !important;
  text-align: center !important;
  vertical-align: middle !important;
  font-size: 14pt !important;
}

/* Management Section */
.management-row {
  background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
}

.management-cell {
  background: #9c27b0 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 12px !important;
  border: 1px solid #7b1fa2 !important;
}

/* Teachers Section */
.teachers-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
}

.teachers-header-cell {
  background: #4caf50 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 14px 12px !important;
  border: 1px solid #388e3c !important;
  text-align: center !important;
}

.teachers-subheader {
  background: linear-gradient(135deg, #81c784 0%, #66bb6a 100%);
}

.sub-header-cell {
  background: #81c784 !important;
  color: white !important;
  font-weight: 500 !important;
  padding: 10px 8px !important;
  border: 1px solid #4caf50 !important;
  text-align: center !important;
}

/* Subject Rows */
.subject-row {
  transition: all 0.2s ease;
}

.subject-row:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  transform: translateY(-1px);
}

.row-highlight {
  background-color: rgba(0, 0, 0, 0.02);
}

.subject-name {
  text-align: left !important;
  padding: 8px 6px !important;
  min-width: 200px;
  font-size: 13pt !important;
}

/* Support Staff */
.support-staff-row {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.support-staff-cell {
  background: #ff9800 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 12px !important;
  border: 1px solid #f57c00 !important;
}

/* Total Summary */
.total-summary-row {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
}

.total-summary-cell {
  background: #e91e63 !important;
  color: white !important;
  font-weight: 700 !important;
  padding: 16px 12px !important;
  border: 1px solid #c2185b !important;
  text-align: center !important;
}

/* Employee Section */
.employee-header-row {
  background: linear-gradient(135deg, #607d8b 0%, #546e7a 100%);
}

.employee-header-cell {
  background: #607d8b !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 12px !important;
  border: 1px solid #546e7a !important;
  text-align: center !important;
}

.employee-data-row {
  background: linear-gradient(135deg, #78909c 0%, #607d8b 100%);
}

.employee-data-cell {
  background: #78909c !important;
  color: white !important;
  font-weight: 500 !important;
  padding: 12px !important;
  border: 1px solid #607d8b !important;
  text-align: center !important;
}

/* Data Cells */
.data-cell {
  padding: 8px 6px !important;
  border: 1px solid #e0e0e0 !important;
  vertical-align: middle !important;
  font-size: 13pt !important;
}

/* Signature Section */
.signature-section {
  margin-top: 3rem;
  padding-top: 2rem;
}

.signature-box {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 2rem;
  background: #fafafa;
}

.signature-line {
  border-bottom: 1px dotted #333;
  padding-bottom: 4px;
  margin-bottom: 1rem;
}

/* Report Footer */
.report-footer {
  margin-top: 2rem;
  padding-top: 1rem;
  font-size: 12pt;
}

.report-footer ul {
  padding-left: 20px;
  margin: 0;
}

.report-footer li {
  margin-bottom: 6px;
}

/* Cards and UI Elements */
.v-card {
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.v-chip {
  font-weight: 500 !important;
  border-radius: 20px !important;
}

/* Buttons */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.v-btn:hover {
  transform: translateY(-1px);
}

/* System Bar */
.v-system-bar {
  border-radius: 0 0 12px 12px !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .page {
    margin: 0 16px;
    padding: 1.5cm 1cm;
  }
}

@media (max-width: 768px) {
  .page {
    margin: 0 8px;
    padding: 1cm 0.5cm;
  }
  
  .head {
    font-size: 16pt;
  }
  
  .bold16, .regular16 {
    font-size: 14pt;
  }
  
  .data-cell {
    padding: 6px 4px !important;
    font-size: 11pt !important;
  }
  
  .header-cell {
    padding: 8px 4px !important;
    font-size: 11pt !important;
  }
  
  .signature-box {
    padding: 1rem;
  }
  
  .subject-name {
    min-width: 150px;
    font-size: 11pt !important;
  }
}

@media (max-width: 480px) {
  .subject-name {
    min-width: 120px;
    font-size: 10pt !important;
  }
  
  .head {
    font-size: 14pt;
  }
  
  .table-wrapper {
    margin: 0 -8px;
  }
  
  .data-cell {
    padding: 4px 2px !important;
    font-size: 10pt !important;
  }
  
  .header-cell {
    padding: 6px 2px !important;
    font-size: 10pt !important;
  }
}

/* Print Styles */
@media print {
  body {
    background: white !important;
    color: black !important;
    font-size: 11pt !important;
  }
  
  .export-content {
    background: white !important;
    padding: 0 !important;
  }
  
  .page {
    border: none !important;
    border-radius: 0 !important;
    width: 100% !important;
    max-width: none !important;
    min-height: initial !important;
    box-shadow: none !important;
    background: white !important;
    margin: 0 !important;
    padding: 1cm 0.8cm !important;
  }

  .noprint {
    display: none !important;
  }

  .head {
    font-size: 14pt !important;
    color: black !important;
  }

  .regular12 {
    font-size: 11pt !important;
  }

  .regular16 {
    font-size: 11pt !important;
  }

  .bold16 {
    font-size: 11pt !important;
    font-weight: bold !important;
  }

  /* Print Table Styles */
  .manpower-table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  .manpower-table td,
  .manpower-table th {
    border: 1px solid black !important;
    padding: 4px 3px !important;
    font-size: 9pt !important;
  }

  .header-cell {
    background: #d0d0d0 !important;
    color: black !important;
    font-weight: bold !important;
    font-size: 9pt !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .data-cell {
    font-size: 9pt !important;
    padding: 4px 3px !important;
  }

  .subject-name {
    font-size: 9pt !important;
    padding: 4px 3px !important;
    min-width: auto !important;
  }

  .management-cell {
    background: #e8d5e8 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .teachers-header-cell {
    background: #e8f5e8 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .sub-header-cell {
    background: #f0f8f0 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .support-staff-cell {
    background: #fff3e0 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .total-summary-cell {
    background: #fce4ec !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .employee-header-cell,
  .employee-data-cell {
    background: #eceff1 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .signature-box {
    border: 1px solid black !important;
    background: white !important;
    padding: 0.8rem !important;
    font-size: 10pt !important;
  }

  /* Ensure text is readable in print */
  .v-chip {
    background: transparent !important;
    color: black !important;
    border: 1px solid black !important;
    font-size: 8pt !important;
  }

  /* Print Color Adjustments */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* Utility Classes */
.text-nowrap {
  white-space: nowrap;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.border-radius-lg {
  border-radius: 12px !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

.shadow-md {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

/* Animation and Transitions */
.v-enter-active, .v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter, .v-leave-to {
  opacity: 0;
}

/* Loading States */
.v-skeleton-loader {
  border-radius: 8px;
}

/* Error States */
.error-state {
  opacity: 0.6;
  pointer-events: none;
}

/* Success States */
.success-highlight {
  background-color: rgba(76, 175, 80, 0.1);
  border-left: 4px solid #4caf50;
}

/* Accessibility */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.manpower-table:focus-within {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .header-cell {
    background: #000000 !important;
    color: #ffffff !important;
    border: 2px solid #ffffff !important;
  }
  
  .data-cell {
    border: 1px solid #000000 !important;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .v-application:not(.theme--light) .page {
    background: #1e1e1e;
    color: #ffffff;
  }
  
  .v-application:not(.theme--light) .manpower-table {
    background: #2d2d2d;
  }
  
  .v-application:not(.theme--light) .data-cell {
    border-color: #404040 !important;
  }
}

/* Custom Scrollbar */
.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Special Effects */
.gradient-text {
  background: linear-gradient(45deg, #1976d2, #42a5f5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-effect {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Table Enhancement */
.manpower-table >>> thead th {
  position: sticky;
  top: 0;
  z-index: 10;
}

.manpower-table >>> tbody tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.02);
}

.manpower-table >>> tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

/* Enhanced Signature Box */
.signature-box {
  position: relative;
  overflow: hidden;
}

.signature-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
}

/* Status Indicators */
.status-online {
  position: relative;
}

.status-online::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  border: 2px solid white;
}

/* Chip Variations */
.v-chip--small {
  height: 24px !important;
  font-size: 12px !important;
}

.v-chip.v-chip--outlined {
  border-width: 2px !important;
}

/* Enhanced Hover Effects */
.data-cell:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

.v-card:hover .v-icon {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

/* Print Page Breaks */
@media print {
  .subject-row {
    page-break-inside: avoid;
  }

  .total-summary-row {
    page-break-before: avoid;
  }

  .signature-section {
    page-break-before: avoid;
  }

  .employee-header-row,
  .employee-data-row {
    page-break-inside: avoid;
  }
}

/* Loading Animation */
@keyframes shimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.loading-shimmer {
  background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
  background-size: 800px 104px;
  animation: shimmer 1.5s infinite ease-out;
}

/* Custom Icon Styles */
.icon-rotate {
  transition: transform 0.3s ease;
}

.icon-rotate:hover {
  transform: rotate(90deg);
}

/* Enhanced Focus States */
.v-text-field:focus-within {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select:focus-within {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Improved Data Visualization */
.data-positive {
  color: #4caf50 !important;
  font-weight: 600;
}

.data-negative {
  color: #f44336 !important;
  font-weight: 600;
}

.data-neutral {
  color: #ff9800 !important;
  font-weight: 500;
}

/* Progress Indicators */
.progress-bar {
  height: 4px;
  background: linear-gradient(90deg, #4caf50, #2196f3);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: progress-shine 2s infinite;
}

@keyframes progress-shine {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Enhanced Error Styling */
.error-highlight {
  border-left: 4px solid #f44336;
  background-color: rgba(244, 67, 54, 0.05);
  padding: 8px 12px;
  border-radius: 4px;
}

/* Success Styling */
.success-highlight {
  border-left: 4px solid #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
  padding: 8px 12px;
  border-radius: 4px;
}

/* Warning Styling */
.warning-highlight {
  border-left: 4px solid #ff9800;
  background-color: rgba(255, 152, 0, 0.05);
  padding: 8px 12px;
  border-radius: 4px;
}

/* Enhanced Mobile Experience */
@media (max-width: 600px) {
  .v-system-bar {
    height: 80px !important;
  }
  
  .v-system-bar .v-btn {
    min-width: auto !important;
    padding: 8px !important;
  }
  
  .v-system-bar .v-btn .v-btn__content {
    flex-direction: column;
    font-size: 10px;
  }
  
  .v-system-bar .v-icon {
    margin-bottom: 2px !important;
    margin-right: 0 !important;
  }
}

/* Enhanced Typography */
.text-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
}

.text-subtitle {
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
}

.text-body {
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
}

.text-caption-enhanced {
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 1.4 !important;
  opacity: 0.7;
}

/* Enhanced Grid System */
.grid-container {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

/* Enhanced Flexbox Utilities */
.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.flex-start {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.flex-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Enhanced Spacing */
.spacing-xs { margin: 4px; }
.spacing-sm { margin: 8px; }
.spacing-md { margin: 16px; }
.spacing-lg { margin: 24px; }
.spacing-xl { margin: 32px; }

.padding-xs { padding: 4px; }
.padding-sm { padding: 8px; }
.padding-md { padding: 16px; }
.padding-lg { padding: 24px; }
.padding-xl { padding: 32px; }

/* Enhanced Border Radius */
.rounded-xs { border-radius: 2px; }
.rounded-sm { border-radius: 4px; }
.rounded-md { border-radius: 8px; }
.rounded-lg { border-radius: 12px; }
.rounded-xl { border-radius: 16px; }
.rounded-full { border-radius: 50%; }

/* Enhanced Shadow System */
.elevation-1 { box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24); }
.elevation-2 { box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23); }
.elevation-3 { box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23); }
.elevation-4 { box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22); }
.elevation-5 { box-shadow: 0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22); }

/* Performance Optimizations */
.gpu-acceleration {
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
}

.will-change-transform {
  will-change: transform;
}

.will-change-opacity {
  will-change: opacity;
}

/* Print Optimizations */
@media print {
  .print-break-before { page-break-before: always; }
  .print-break-after { page-break-after: always; }
  .print-break-inside-avoid { page-break-inside: avoid; }
  .print-break-inside-auto { page-break-inside: auto; }
}
</style>




